if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

          // The thumbnail_slider gallery layout hides every media item
          // except the active one (see the theme CSS rule
          // ".product--thumbnail_slider .product__media-item:not(.is-active)"),
          // so the slider's native scroll-based arrow paging has nothing to
          // scroll between — there's only ever one item with any width.
          // Step through media items directly instead, the same way a
          // thumbnail click already does.
          if (this.elements.viewer.prevButton) {
            this.elements.viewer.prevButton.addEventListener('click', () => this.stepActiveMedia(-1));
          }
          if (this.elements.viewer.nextButton) {
            this.elements.viewer.nextButton.addEventListener('click', () => this.stepActiveMedia(1));
          }
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        this.syncVariantFromMedia(event.detail.currentElement);
      }

      // Steps the active media forward/back by `direction` (1 or -1) among
      // all gallery media items, wrapping around at the ends. Used by the
      // main viewer's prev/next arrow buttons — see the note in the
      // constructor for why this can't rely on the slider actually scrolling.
      stepActiveMedia(direction) {
        // Scoped to the slide wrapper itself: the viewer also contains a
        // lightbox/zoom toggle per slide that carries its own
        // [data-media-id], so an unscoped query double-counts each photo.
        const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        if (items.length < 2) return;
        const activeIndex = items.findIndex((element) => element.classList.contains('is-active'));
        const currentIndex = activeIndex === -1 ? 0 : activeIndex;
        const nextIndex = (currentIndex + direction + items.length) % items.length;
        this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
      }

      // Keeps the colour/variant picker's visual selection in sync when the
      // shopper changes the active gallery image via the arrows, a
      // thumbnail, or swipe — rather than only syncing picker -> gallery
      // (the reverse already worked).
      //
      // Deliberately visual-only: this sets the radio/option's checked state
      // directly instead of dispatching a 'change' event. Dispatching
      // 'change' would trigger Dawn's product-info.js variant-swap pipeline
      // (an AJAX refetch that also re-renders price/URL/etc.), which in turn
      // re-sorts the gallery's photo list to match the order of whatever it
      // just fetched — scrambling the stable order stepActiveMedia() depends
      // on. That's what caused arrows to appear to "swap" only two photos or
      // jump back to the start. Just browsing photos with the arrows
      // shouldn't trigger a full variant switch; an actual swatch click
      // still fires a real 'change' event and goes through that pipeline
      // normally, correctly updating price/URL/etc.
      syncVariantFromMedia(slideElement) {
        const optionValueId = slideElement?.dataset?.variantOptionValueId;
        if (!optionValueId) return;

        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects) return;

        const target = variantSelects.querySelector(`[data-option-value-id="${optionValueId}"]`);
        if (!target) return;

        if (target.tagName === 'INPUT') {
          if (target.checked) return;
          target.checked = true;
        } else if (target.tagName === 'OPTION') {
          const select = target.closest('select');
          if (!select || select.value === target.value) return;
          target.selected = true;
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.syncVariantFromMedia(activeMedia);

        // Dawn's own product-info.js calls setActiveMedia(id, true) on every
        // variant change (including the synthetic 'change' event dispatched
        // by syncVariantFromMedia() above when browsing via arrows/thumbnails),
        // and prepend=true normally reorders the gallery DOM to bring the new
        // photo to the front. In the thumbnail/thumbnail_slider layouts this
        // theme uses, visibility is controlled purely by the .is-active class
        // (see the note in the constructor), so that reordering serves no
        // purpose here — it only scrambles the stable order stepActiveMedia()
        // depends on, which is what caused arrows to appear to "swap" or jump
        // between only two photos. Skip it for those layouts; leave the
        // original behaviour intact for any other layout where it would
        // still matter.
        if (prepend && !this.dataset.desktopLayout.includes('thumbnail')) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
